import { useState, type ReactNode, type RefObject } from 'react';
import { PageFieldSheet } from '../../shared/page_field_sheet.js';
import { returnFocusUnlessMoved } from '../../shared/return_focus.js';
import type { FlightPlannerCopy } from './planner_copy.js';
import type { PlannerLeg } from './planner_models.js';
import { SkirtConfirm, SkirtReset } from './dates_skirt.js';

/** A cidade de um IATA, quando o host a conhece — ver `PlannerFormProps.cityOfAirport`. */
type CityOfAirport = ((iata: string) => string | null | undefined) | undefined;

/**
 * O CABEÇALHO da folha do QUANDO (#3446): o título por modo e a linha de
 * contexto (a posição do trecho no multi). O miolo é o `TravelCalendar` em
 * `layout="sheet"` — a linha dos dias, a coluna infinita e o rodapé são dele;
 * o planner só entrega a cor dos dias e o rodapé (`WhenSheetFooter`).
 */
export type WhenSheetSlots = Readonly<{
  readonly title: string;
  /** A linha de contexto sob o título (a posição do trecho no multi). */
  readonly subtitle?: string;
}>;

/**
 * A FOLHA de tela cheia do QUANDO no celular (#2653): a mesma casca das datas
 * do hotel, do ônibus e do carro (`PageFieldSheet`, #5662) com o corpo do
 * popover dentro. Fechar pela seta, pelo véu ou por Escape é o `onClose` (o
 * mesmo `onDismiss` + fechar do popover). O foco volta ao gatilho só se não
 * pousou noutro lugar — no preenchimento guiado o Confirmar já o pôs no Buscar
 * (`returnFocusUnlessMoved`).
 */
export function WhenSheet({
  open,
  slots,
  backLabel,
  onClose,
  returnFocusTo,
  children,
}: Readonly<{
  readonly open: boolean;
  readonly slots: WhenSheetSlots;
  readonly backLabel: string;
  readonly onClose: () => void;
  readonly returnFocusTo: RefObject<HTMLElement | null>;
  readonly children: ReactNode;
}>) {
  return (
    <PageFieldSheet
      open={open}
      title={slots.title}
      {...(slots.subtitle ? { subtitle: slots.subtitle } : {})}
      backLabel={backLabel}
      onClose={onClose}
      returnFocusTo={returnFocusUnlessMoved(returnFocusTo)}
    >
      <div className="fpl-sheet">{children}</div>
    </PageFieldSheet>
  );
}

/**
 * O rodapé pregado da folha binária: a saia de estado (a escolha e a frase do
 * agente, com o "N datas analisadas" na linha da economia — desenho da #5662) e a linha de
 * ações. O Confirmar é FIXO ("Confirmar datas"; "Confirmar e buscar" quando ele
 * busca, #4620): no popover ele nomeia o par ("Confirmar 11–20 ago") porque é
 * o único texto da saia; aqui o par já está escrito uma linha acima, e o
 * rótulo mudando de tamanho fazia o botão pular.
 * Redefinir aparece com a ida escolhida, como na saia do popover.
 *
 * Somente ida (`oneWay`) fecha no toque do dia, como o popover: o Confirmar só
 * tem função para quem abriu com o campo JÁ preenchido e quer sair sem mexer.
 * Sem valor na abertura (a primeira vez) a saia fica sem botão — e Redefinir a
 * esvaziar de novo também o tira. A abertura é a montagem: a folha não existe
 * fechada, e ler a data na hora do toque acenderia o botão durante a saída.
 */
export function WhenSheetFooter({
  status,
  hasOutbound,
  oneWay,
  confirmDisabled,
  resetDates,
  searches,
  confirm,
  copy,
}: Readonly<{
  readonly status: ReactNode;
  readonly hasOutbound: boolean;
  readonly oneWay: boolean;
  readonly confirmDisabled: boolean;
  readonly resetDates: () => void;
  /** O Confirmar também busca (o preenchimento guiado com a rota dada). */
  readonly searches: boolean;
  /** Fecha a folha e responde o QUANDO. */
  readonly confirm: () => void;
  readonly copy: FlightPlannerCopy;
}>) {
  const [filledAtOpen] = useState(hasOutbound);
  const showConfirm = !oneWay || (filledAtOpen && hasOutbound);
  return (
    <>
      {status}
      {(hasOutbound || showConfirm) && (
        <div className="fpl-sheet-actions">
          {hasOutbound && (
            <SkirtReset className="rounded-full" onClick={resetDates}>
              {copy.reset}
            </SkirtReset>
          )}
          {showConfirm && (
            <SkirtConfirm
              className="flex-1 rounded-full"
              disabled={confirmDisabled}
              onClick={confirm}
            >
              {searches ? copy.confirmAndSearch : copy.confirmDates}
            </SkirtConfirm>
          )}
        </div>
      )}
    </>
  );
}

/**
 * O TÍTULO da folha por modo (#3446, desenho do Leonardo): "Ida e volta", "Ida
 * para {cidade}" ou só "Ida" quando o destino ainda não existe. Sem abas
 * (`tripType` ausente) o card não tem modo a nomear e a folha segue "Quando".
 *
 * A cidade vem do host pelo IATA (`cityOf`: o `cityByAirport` do domain, a
 * mesma autoridade do rótulo do combobox). Quando ele não conhece o código, ou
 * o host não passa a função, cai no rótulo de display do bloco sem o código — ele chega como "GRU São Paulo" (o combobox)
 * ou "Nova York · JFK" (fixtures antigas do wire).
 */
export function whenSheetTitle({
  tripType,
  destination,
  destinationLabel,
  cityOf,
  copy,
}: Readonly<{
  readonly tripType: 'round_trip' | 'one_way' | undefined;
  readonly destination: string;
  readonly destinationLabel: string | undefined;
  readonly cityOf: CityOfAirport;
  readonly copy: FlightPlannerCopy;
}>): string {
  if (tripType === undefined) return copy.when;
  if (tripType === 'round_trip') return copy.tripTypeRoundTrip;
  const code = destination.trim().toUpperCase();
  const city = (code ? cityOf?.(code) : undefined) ?? stripAirportCode(destinationLabel);
  return city ? copy.sheetOneWayTo({ city }) : copy.sheetOneWay;
}

/**
 * O cabeçalho da folha de UM TRECHO do multi (#3446): o título da Somente ida
 * para o destino do trecho ("Ida para Miami", ou "Ida" sem destino) e, sob
 * ele, a posição e a origem — "2 de 3 · saindo de JFK". O código IATA fica de
 * propósito: a cidade pediria o artigo certo (do Rio, de São Paulo), e o nome
 * chega cru do domain; o código é curto e não corta.
 */
export function legSheetHeading(
  leg: PlannerLeg,
  index: number,
  total: number,
  copy: FlightPlannerCopy,
  cityOf: CityOfAirport,
): WhenSheetSlots {
  const title = whenSheetTitle({
    tripType: 'one_way',
    destination: leg.destination,
    destinationLabel: leg.destinationLabel,
    cityOf,
    copy,
  });
  const n = String(index + 1);
  const code = leg.origin.trim().toUpperCase();
  const subtitle = code
    ? copy.sheetLegFrom({ n, total: String(total), code })
    : copy.sheetLeg({ n, total: String(total) });
  return { title, subtitle };
}

const AIRPORT_CODE = /^[A-Z]{3}$/;

/** Tira o código do FIM do rótulo ("Paris · CDG", "Paris (CDG)") lendo por índice —
 *  a regex `\s*[·(]\s*[A-Z]{3}\)?$` reescaneava cada corrida de espaço (S5852). */
function withoutTrailingCode(label: string): string {
  const core = label.endsWith(')') ? label.slice(0, -1) : label;
  if (!AIRPORT_CODE.test(core.slice(-3))) return label;
  const marked = core.slice(0, -3).trimEnd();
  const mark = marked.slice(-1);
  return mark === '·' || mark === '(' ? marked.slice(0, -1) : label;
}

function stripAirportCode(label: string | undefined): string | undefined {
  const city =
    label === undefined ? undefined : withoutTrailingCode(label.replace(/^[A-Z]{3}\s+/, '')).trim();
  return city || undefined;
}
